import { useEffect, useState } from "react";
import { Box, IconButton, Paper, Stack, Typography, alpha } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FlipRoundedIcon from "@mui/icons-material/FlipRounded";

/*
  FlashcardsTab
  Flip-card UI with prev/next nav and progress dots. Cards are
  driven by `studyPack.flashcards: [{ front, back }]`.
*/

export default function FlashcardsTab({ studyPack }) {
  const cards = Array.isArray(studyPack?.flashcards) ? studyPack.flashcards : [];
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    setIdx(0);
    setFlipped(false);
  }, [cards.length, studyPack?.updatedAt]);

  if (!cards.length) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
        <Typography variant="body2" color="text.secondary">
          No flashcards yet. Ask Copilot for "more flashcards" in the Chat tab to generate some.
        </Typography>
      </Paper>
    );
  }

  const c = cards[idx];
  const total = cards.length;
  const go = (delta) => {
    setFlipped(false);
    setIdx((prev) => Math.max(0, Math.min(total - 1, prev + delta)));
  };

  return (
    <Stack spacing={2} alignItems="center">
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Card {idx + 1} / {total}
      </Typography>

      <Box
        onClick={() => setFlipped((f) => !f)}
        sx={{
          width: "100%",
          maxWidth: 640,
          aspectRatio: "5 / 3",
          perspective: "1200px",
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0)",
            transition: "transform .6s",
          }}
        >
          {/* Front */}
          <Paper
            variant="outlined"
            sx={(t) => ({
              position: "absolute",
              inset: 0,
              borderRadius: 4,
              p: 3,
              backfaceVisibility: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundImage: `linear-gradient(135deg, ${alpha(
                t.palette.primary.main,
                0.06,
              )}, ${alpha(t.palette.info.main, 0.04)})`,
              borderColor: alpha(t.palette.primary.main, 0.25),
            })}
          >
            <Stack spacing={1.2} alignItems="center" textAlign="center">
              <Typography variant="caption" color="primary.main" fontWeight={700} letterSpacing={1}>
                QUESTION
              </Typography>
              <Typography variant="h5" fontWeight={600} sx={{ maxWidth: 560 }}>
                {c.front || c.question || "—"}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                <FlipRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  Click to flip
                </Typography>
              </Stack>
            </Stack>
          </Paper>
          {/* Back */}
          <Paper
            variant="outlined"
            sx={(t) => ({
              position: "absolute",
              inset: 0,
              borderRadius: 4,
              p: 3,
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundImage: `linear-gradient(135deg, ${alpha(
                t.palette.success.main,
                0.08,
              )}, ${alpha(t.palette.success.light, 0.06)})`,
              borderColor: alpha(t.palette.success.main, 0.3),
            })}
          >
            <Stack spacing={1.2} alignItems="center" textAlign="center">
              <Typography variant="caption" color="success.dark" fontWeight={700} letterSpacing={1}>
                ANSWER
              </Typography>
              <Typography variant="h5" fontWeight={600} sx={{ maxWidth: 560 }}>
                {c.back || c.answer || "—"}
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
        <IconButton onClick={() => go(-1)} disabled={idx === 0}>
          <ChevronLeftRoundedIcon />
        </IconButton>
        <Stack direction="row" spacing={0.6}>
          {cards.slice(0, Math.min(total, 12)).map((_, i) => (
            <Box
              key={i}
              sx={(t) => ({
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: i === idx ? t.palette.primary.main : alpha(t.palette.primary.main, 0.2),
                transition: "background-color .15s ease",
              })}
            />
          ))}
          {total > 12 ? (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
              +{total - 12}
            </Typography>
          ) : null}
        </Stack>
        <IconButton onClick={() => go(1)} disabled={idx === total - 1}>
          <ChevronRightRoundedIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}
